import { Component,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  OnDestroy,
  OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GlobalService } from '../../global/services/global.service';
import { ModalFactoryService } from '../../global/services/modal-factory.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { SyncEntityClassificationService } from './sync-entity-classification.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-sync-entity-classification',
    templateUrl: './sync-entity-classification.component.html',
    styleUrls: ['./sync-entity-classification.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SyncEntityClassificationComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  confirmConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to proceed?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...',
  };

  filterParams: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  globalService: any = {};
  workspaceLabel = '';
  /** Grid data: array of { data: { ... } } */
  gridData: any[] = [];
  /** Processed rows for save/sync */
  syncSaveDtls: any[] = [];
  modalName = '';
  saveLoading = false;
  private isInitialized = false;

  constructor(
    private syncService: SyncEntityClassificationService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwModalFactory: ModalFactoryService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    const gp = header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.gridData = this.resolveGridRows(activeScreen);
    this.syncSaveDtls = [];
    this.buildSyncSaveDtls();

    // Legacy: vm.modalName = ModalFactory.response.data.modalScreen.modal_name.
    // The grid action name ('saveEntities' | 'reviewEntities' | 'syncEntitesToGTW'
    // | 'acceptSave') decides which confirmation text shows and which save_type is
    // sent, since all four actions resolve to this same custom element.
    // CeHostDialog bridges that name flat onto activeScreen.data.modal_name;
    // ModalFactoryService holds the /getScreenModal response as a fallback.
    const asData = (activeScreen as any).data || {};
    this.modalName =
      asData.modal_name ||
      asData.modalName ||
      this.gtwModalFactory.modalFactory?.response?.modalScreen?.modal_name ||
      gp?.modalScreen?.modal_name ||
      '';

    // Legacy: returnClobSettingsObj['entry_path'] = workspaceFactory.activeScreen.label
    this.workspaceLabel =
      (activeScreen as any)?.label ??
      gp?.workspaceLabel ??
      gp?.entry_path ??
      (activeScreen as any)?.entry_path ??
      '';
    this.updateConfirmMessage();
    this.isInitialized = false;
    this.cdr.markForCheck();
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private formatDate(value: any, format: string): string {
    if (value == null) return '';
    const d = typeof value === 'string' ? new Date(value) : value;
    if (!(d instanceof Date) || isNaN(d.getTime())) return '';
    if (format === 'MM/dd') {
      const m = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      return `${m}/${day}`;
    }
    if (format === 'MM/dd/yyyy') {
      const m = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      const y = d.getFullYear();
      return `${m}/${day}/${y}`;
    }
    return '';
  }

  /**
   * Legacy resolve was `gridData` — the whole grid multi-selection, an array of
   * `{ row_id, data: <row> }`. CeHostDialog bridges that selection onto
   * activeScreen.data.gridData, while activeScreen.data.rowData only carries the
   * FIRST selected row, so reading rowData would save one entity out of many.
   */
  private resolveGridRows(activeScreen: any): any[] {
    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(gridPayload) && gridPayload.length > 0) {
      return gridPayload;
    }
    if (gridPayload && typeof gridPayload === 'object') {
      if (Array.isArray(gridPayload.gridData) && gridPayload.gridData.length > 0) {
        return gridPayload.gridData;
      }
      if (gridPayload.rowData && typeof gridPayload.rowData === 'object') {
        return [gridPayload.rowData];
      }
      const values = Object.values(gridPayload);
      const isBatchMap =
        values.length > 0 && values.every((v: any) => v && typeof v === 'object' && v.data && typeof v.data === 'object');
      if (isBatchMap) {
        return values;
      }
    }
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(rawRow)) {
      return rawRow;
    }
    if (rawRow && typeof rawRow === 'object' && Object.keys(rawRow).length > 0) {
      return [rawRow];
    }
    return [];
  }

  private buildSyncSaveDtls(): void {
    this.syncSaveDtls = [];
    if (!this.gridData || this.gridData.length === 0) return;
    const tempGridData = _.clone(this.gridData) as any[];
    for (let i = 0; i < tempGridData.length; i++) {
      const item = tempGridData[i];
      const data = item?.data ?? item;
      if (!data) continue;
      const gridObj: any = {
        object_id: data.object_id,
        status: data.STATUS,
        guid: data.GUID,
        leid: data.ENTITY_ID,
        cdr_number: data.ALTERNATE_ENTITY_ID,
        reporting_period: data.REPORTING_PERIOD,
        childId: data.CHILD_ID,
        stub: data.STUB,
        taxYear: data.TAX_YEAR,
        scenario: data.SCENARIO,
        startDate: this.formatDate(data.START_DATE, 'MM/dd'),
        EndDate: this.formatDate(data.END_DATE, 'MM/dd/yyyy'),
        taxType: data.TAX_TYPE,
        ho_leid: data.HO_LEID,
        ho_cdr_number: data.HO_ALTERNATE_ENTITY_ID,
        ho_reporting_period: data.HO_REPORTING_PERIOD,
        HoCountryIncorp: data.HO_COUNTRY_INCORPORATED,
        parent_leid: data.PARENT_LEID,
        parent_cdr_number: data.PARENT_ALTERNATE_ENTITY_ID,
        parent_reporting_period: data.PARENT_REPORTING_PERIOD,
        classDesc: data.CLASSIFICATION_DESC,
        classification: data.CLASSIFICATION,
        acqDate: this.formatDate(data.ACQUISITION_DATE, 'MM/dd'),
        incorpDate: this.formatDate(data.DATE_INCORPORATED, 'MM/dd/yyyy'),
        goldStartDate: this.formatDate(data.GOLD_START_DATE, 'MM/dd'),
        goldEndDate: this.formatDate(data.GOLD_END_DATE, 'MM/dd/yyyy'),
        dcsEntity: data.DCS_ENTITY,
        firEntity: data.FIR_ENTITY,
        stdReason: data.STANDARD_REASON,
        filingResp: data.FILING_ENTITY,
        comments: data.FILING_COMMENTS,
        syncFlag: data.GTW_SYNC_FLAG,
        reviewFlag: data.ACCEPT_FLAG,
        RECORD_TYPE: data.RECORD_TYPE,
      };
      this.syncSaveDtls.push(gridObj);
    }
  }

  private updateConfirmMessage(): void {
    switch (this.modalName) {
      case 'saveEntities':
        this.confirmConfig = {...this.confirmConfig, title: 'Confirm', message: 'Are you sure you want to save these entities?'};
        break;
      case 'syncEntitesToGTW':
        this.confirmConfig = {...this.confirmConfig, title: 'Confirm', message: 'Are you sure you want to sync these entities to GTW?'};
        break;
      case 'reviewEntities':
        this.confirmConfig = {...this.confirmConfig, title: 'Confirm', message: 'Are you sure you want to Review these entities & Save?'};
        break;
      case 'acceptSave':
        this.confirmConfig = {...this.confirmConfig, title: 'Confirm', message: 'Are you sure you want to accept this change & Save?'};
        break;
      default:
        this.confirmConfig = {...this.confirmConfig, title: 'Confirm', message: 'Are you sure you want to proceed?'};
    }
    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    if (!this.syncSaveDtls || this.syncSaveDtls.length === 0) return;
    this.isInitialized = true;
    this.cdr.detectChanges();
  }

  /** Legacy compared grid values with `==`; reporting periods arrive as both number and string. */
  private looseEquals(a: any, b: any): boolean {
    if (a == null && b == null) return true;
    if (a == null || b == null) return false;
    return String(a) === String(b);
  }

  private buildReturnObj(row: any): any {
    // Legacy parity, quirk included: entityClassificationCtrl.js:137-143 reads
    // `}if(` — two statements, not `else if`. A BRANCH row is set to 'BRANCH'
    // and then immediately overwritten by the COMPANY_CODE if/else, so legacy
    // only ever sends HO | COMPANY CODE | NONHO.
    let entityType: string;
    if (
      this.looseEquals(row.leid, row.ho_leid) &&
      this.looseEquals(row.cdr_number, row.ho_cdr_number) &&
      this.looseEquals(row.reporting_period, row.ho_reporting_period)
    ) {
      entityType = 'HO';
    } else if (row.RECORD_TYPE === 'COMPANY_CODE') {
      entityType = 'COMPANY CODE';
    } else {
      entityType = 'NONHO';
    }
    return {
      leid: row.leid,
      cdr_no: row.cdr_number,
      reporting_period: row.reporting_period,
      ho_leid: row.ho_leid,
      ho_cdr_no: row.ho_cdr_number,
      ho_reporting_period: row.ho_reporting_period,
      parent_leid: row.parent_leid,
      parent_cdr_no: row.parent_cdr_number,
      parent_reporting_period: row.parent_reporting_period,
      guid: row.guid,
      std_reason: row.stdReason,
      comments: row.comments,
      filing: row.filingResp,
      review_flag: row.reviewFlag,
      sync_flag: row.syncFlag,
      entity_type: entityType,
    };
  }

  /**
   * Lock descriptors for the selected rows, mapped the same way
   * JsonObjectFactoryService builds `checkLockedbj`, using this grid's own
   * column names (ENTITY_ID / ALTERNATE_ENTITY_ID) with the standard fallbacks.
   */
  private buildCheckLockedObjs(): any[] {
    if (!this.gridData || this.gridData.length === 0) return [];
    const gp = this.globalService?.globalParams || {};
    const out: any[] = [];
    for (const item of this.gridData) {
      const data = item?.data ?? item;
      if (!data) continue;
      out.push({
        leid: data.ENTITY_ID || data.LEID || data.SOURCE_LEID,
        me_code: data.ME_CODE || data.ME || data.SOURCE_ME_CODE,
        cdr_number: data.ALTERNATE_ENTITY_ID || data.CDR_NO || data.TAX_CODE,
        consol_group_key: data.CONSOL_GROUP_KEY || data.FILING_GROUP,
        reporting_period: data.REPORTING_PERIOD || data.SOURCE_REPORTING_PERIOD,
        combination_key: data.COMBINATION_KEY || data.COMPL_COMBINATION_KEY || data.HO_COMBINATION_KEY,
        scenario_key: data.SCENARIO_KEY || this.filterParams?.scenario || gp.scenario,
      });
    }
    return out;
  }

  onConfirm(): void {
    const isAcceptSave = this.modalName === 'saveEntities' || this.modalName === 'reviewEntities';
    if (isAcceptSave) {
      this.confirmAcceptSave();
    } else {
      this.confirmSync();
    }
  }

  private confirmAcceptSave(): void {
    const alreadySynced = this.syncSaveDtls.filter((o) => o.status === 'SYNCED');
    if (alreadySynced.length > 0) {
      // Legacy dismisses the modal before surfacing the error.
      this.onCancel();
      this.gtwSnackbarService.openSnackBar("Can't Save/Review the synced entities", 'gtw-snackbar--red');
      return;
    }
    const saveType = this.modalName;
    const ecAcceptSaveDtls = this.syncSaveDtls.map((row) => this.buildReturnObj(row));
    const returnClobSettingsObj = {
      sso_id: this.ssoId,
      entry_path: this.workspaceLabel,
      screen_key: this.globalService?.globalParams?.screen_key ?? '',
      save_type: saveType,
    };
    this.sendDetails(returnClobSettingsObj, ecAcceptSaveDtls, 'Entity Classification has been successfully saved/updated');
  }

  private confirmSync(): void {
    const saveType = this.modalName;
    const ecSaveDtls = this.syncSaveDtls.map((row) => this.buildReturnObj(row));
    const returnClobSettingsObj = {
      sso_id: this.ssoId,
      entry_path: this.workspaceLabel,
      screen_key: this.globalService?.globalParams?.screen_key ?? '',
      save_type: saveType,
    };
    this.sendDetails(returnClobSettingsObj, ecSaveDtls, 'Entity Classification has been successfully saved/updated');
  }

  private sendDetails(
    returnClobSettingsObj: { sso_id: string; entry_path?: string; screen_key?: string; save_type: string },
    details: any[],
    message: string
  ): void {
    this.saveLoading = true;
    this.confirmConfig = {...this.confirmConfig, loading: true};
    this.cdr.detectChanges();

    this.syncService
      .saveEntityClassification(
        this.baseUrl,
        returnClobSettingsObj,
        details,
        this.globalService?.globalParams || {},
        this.filterParams,
        this.buildCheckLockedObjs()
      ).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.saveLoading = false;
          this.confirmConfig = {...this.confirmConfig, loading: false};
          if (result?.errorMessage && result.errorMessage !== 'null') {
            this.onCancel();
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.saveLoading = false;
          this.confirmConfig = {...this.confirmConfig, loading: false};
          this.onCancel();
          this.gtwSnackbarService.openSnackBar(err?.message || 'Error saving.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  onCancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
